import * as signalR from '@microsoft/signalr';
import { renewSession } from './api';
import type {
  AssistantMessageReadyMessage,
  BuildStateMessage,
  ErrorMessage,
  JobStatusMessage,
  LayoutReadyMessage,
  MilestoneMessage,
  PipelineConnectionStatus,
  PipelineEventHandlers,
  SystemMessageMessage,
  UsageUpdate,
  VerboseLogMessage,
} from '../models/pipelineEvents';

const MaxReconnectDelayMs = 30_000;

/**
 * The wait before connecting again after `failures` failed attempts since the connection last worked: a random point
 * in the upper half of a step that doubles from 1 s and stops at 30 s, so clients dropped together spread out.
 * `random` returns a number in [0, 1).
 */
export function reconnectDelayMs(failures: number, random: () => number = Math.random): number {
  const step = Math.min(1000 * 2 ** failures, MaxReconnectDelayMs);
  return step / 2 + (step / 2) * random();
}

export interface ProjectFollower {
  /** The handlers of every current subscriber, read again for each event. */
  subscribers: () => Iterable<PipelineEventHandlers>;
  onStatus: (status: PipelineConnectionStatus) => void;
}

/** `ProjectNotFound`: the project is unknown or another user's, and asking again gives the same answer. */
type JoinProjectGroupResult = 'Joined' | 'ProjectNotFound';

/**
 * Opens one hub connection, joins the project's group and delivers its events to every subscriber until the returned
 * function is called or the server refuses the project. After any drop it connects again after `reconnectDelayMs`,
 * with no time limit, and renews the session when it has expired.
 */
export function followProject(projectId: string, follower: ProjectFollower): () => void {
  const connection = createPipelineHubConnection();
  let following = true;
  let failures = 0;
  let missedEvents = false;
  let retryTimer: number | undefined;

  const each = (call: (handlers: PipelineEventHandlers) => void) => {
    for (const handlers of Array.from(follower.subscribers())) call(handlers);
  };

  const deliver = <T extends { projectId: string }>(
    event: string,
    handle: (handlers: PipelineEventHandlers, message: T) => void,
  ) =>
    connection.on(event, (message: T) => {
      if (message.projectId === projectId) each((handlers) => handle(handlers, message));
    });

  deliver<MilestoneMessage>('ReceiveMilestone', (h, m) => h.onMilestone?.(m));
  deliver<VerboseLogMessage>('ReceiveVerboseLog', (h, m) => h.onVerboseLog?.(m));
  deliver<AssistantMessageReadyMessage>('ReceiveAssistantMessageReady', (h, m) => h.onAssistantMessageReady?.(m));
  deliver<SystemMessageMessage>('ReceiveSystemMessage', (h, m) => h.onSystemMessage?.(m));
  deliver<UsageUpdate>('ReceiveUsage', (h, m) => h.onUsage?.(m));
  deliver<ErrorMessage>('ReceiveError', (h, m) => h.onError?.(m));
  deliver<BuildStateMessage>('ReceiveBuildState', (h, m) => h.onBuildStateChanged?.(m));
  deliver<LayoutReadyMessage>('ReceiveLayoutReady', (h, m) => h.onLayoutReady?.(m));
  deliver<JobStatusMessage>('ReceiveJobStatus', (h, m) => h.onJobStatus?.(m));

  const connectLater = () => {
    missedEvents = true;
    follower.onStatus('reconnecting');
    retryTimer = window.setTimeout(connect, reconnectDelayMs(failures));
  };

  async function connect() {
    try {
      await connection.start();
    } catch {
      if (following) {
        failures++;
        connectLater();
      }
      return;
    }
    let result: JoinProjectGroupResult;
    try {
      result = await connection.invoke<JoinProjectGroupResult>('JoinProjectGroup', projectId);
    } catch {
      failures++;
      // Not connectLater: a started connection is retried by onclose, so this would schedule a second attempt.
      void connection.stop();
      return;
    }
    if (!following) return;
    if (result === 'ProjectNotFound') {
      console.warn(`The server refused pipeline events for project ${projectId}: ${result}`);
      following = false;
      follower.onStatus('refused');
      void connection.stop();
      return;
    }
    failures = 0;
    follower.onStatus('connected');
    if (missedEvents) {
      missedEvents = false;
      each((handlers) => handlers.onReconnected());
    }
  }

  connection.onclose(() => {
    if (following) connectLater();
  });

  follower.onStatus('connecting');
  // Deferred, so a mount that React undoes at once (StrictMode) never opens a connection.
  retryTimer = window.setTimeout(connect, 0);

  return () => {
    following = false;
    window.clearTimeout(retryTimer);
    void connection.stop();
  };
}

function hubUrl(): string {
  if (import.meta.env.DEV) {
    return '/hubs/pipeline';
  }
  const envBaseUrl = import.meta.env.VITE_API_BASE_URL;
  if (envBaseUrl) {
    return `${envBaseUrl}/hubs/pipeline`;
  }
  return `${window.location.protocol}//${window.location.host}/hubs/pipeline`;
}

/** Renews the session and retries once when the server turns a hub request away as unauthenticated. */
class SessionRenewingHttpClient extends signalR.HttpClient {
  private readonly inner = new signalR.DefaultHttpClient(signalR.NullLogger.instance);

  async send(request: signalR.HttpRequest): Promise<signalR.HttpResponse> {
    try {
      return await this.inner.send(request);
    } catch (error) {
      if (!(error instanceof signalR.HttpError && error.statusCode === 401)) throw error;
      await renewSession();
      return this.inner.send(request);
    }
  }
}

function createPipelineHubConnection(): signalR.HubConnection {
  return new signalR.HubConnectionBuilder()
    .withUrl(hubUrl(), { withCredentials: true, httpClient: new SessionRenewingHttpClient() })
    .configureLogging(signalR.LogLevel.Warning)
    .build();
}
